import { useState, useRef } from "react";
import { api } from "@/lib/api";
import PartnerLink from "@/components/PartnerLink";
import { toast } from "sonner";
import { StatusDot } from "@/components/Layout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UploadCloud, FileText, Download } from "lucide-react";

export default function SupportingDocs({ property, data, onChange }) {
  const [target, setTarget] = useState(null);
  const [f, setF] = useState({ issue_date: "", expiry_date: "", served_date: "", note: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();
  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const groups = [...new Set(data.items.map((i) => i.entry.group))];

  const upload = async () => {
    if (!file) return toast.error("Choose a file");
    setBusy(true);
    const fd = new FormData();
    fd.append("doc_key", target.entry.key); fd.append("file", file);
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    try {
      await api.post(`/properties/${property.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${target.entry.name} filed`); setTarget(null); setFile(null); setF({ issue_date: "", expiry_date: "", served_date: "", note: "" }); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const openFile = async (doc) => {
    const { data: d } = await api.get(`/documents/${doc.id}/file`);
    const w = window.open();
    w.document.write(`<iframe src="data:${d.file_mime};base64,${d.file_data}" style="border:0;width:100%;height:100%"></iframe>`);
  };

  return (
    <div className="space-y-5">
      {!data.tenancy && <p data-testid="docs-tenancy-hint" className="text-xs rounded-xl bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2">Tenancy-level documents (agreement, deposit, Right to Rent) are filed against the current tenancy — add it in the Tenancy tab first.</p>}
      {groups.map((g) => (
        <div key={g}>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{g}</p>
          <div className="space-y-2">
            {data.items.filter((i) => i.entry.group === g).map((it) => (
              <div key={it.entry.key} data-testid={`sdoc-${it.entry.key}`} className="bg-card border border-border rounded-2xl p-4 flex items-start gap-3">
                <StatusDot status={it.status === "grey" ? "none" : it.status} className={`h-3 w-3 mt-1.5 ${it.status === "grey" ? "bg-slate-300" : ""}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-sm">{it.entry.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${it.entry.required ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-secondary text-muted-foreground border-border"}`}>{it.entry.required ? "required" : "recommended"}</span>
                    {it.entry.level === "tenancy" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">per tenancy</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{it.entry.explanation}</p>
                  {it.doc && <p className="text-xs text-muted-foreground mt-1">{it.doc.file_name} · uploaded {new Date(it.doc.uploaded_at).toLocaleDateString()}{it.doc.expiry_date ? ` · expires ${it.doc.expiry_date}` : ""}{it.doc.served_date ? ` · served ${it.doc.served_date}` : ""}</p>}
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <span className={`text-xs font-medium ${it.status === "red" ? "text-rose-600" : it.status === "amber" ? "text-amber-600" : it.status === "green" ? "text-emerald-deep" : "text-muted-foreground"}`}>{it.label}</span>
                  <div className="flex gap-1">
                    {it.doc && <button data-testid={`sdoc-view-${it.entry.key}`} onClick={() => openFile(it.doc)} className="text-xs px-2 min-h-[32px] rounded-lg border border-border hover:bg-secondary inline-flex items-center gap-1"><Download size={12} /> View</button>}
                    <button data-testid={`sdoc-upload-${it.entry.key}`} onClick={() => setTarget(it)} className="text-xs px-2 min-h-[32px] rounded-lg bg-navy text-white hover:bg-navy-700 inline-flex items-center gap-1"><UploadCloud size={12} /> {it.doc ? "Replace" : "Upload"}</button>
                  </div>
                  {it.entry.key === "insurance" && <PartnerLink slot="landlord_insurance" label="Get a quote" />}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="bg-card max-w-md" data-testid="sdoc-modal">
          <DialogHeader><DialogTitle className="font-heading text-base">{target?.entry.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input ref={inputRef} type="file" className="hidden" data-testid="sdoc-file-input" onChange={(e) => setFile(e.target.files[0])} />
            <button data-testid="sdoc-dropzone" onClick={() => inputRef.current?.click()} className="w-full border-2 border-dashed border-border rounded-2xl p-6 flex flex-col items-center gap-2 hover:border-emerald-brand transition-colors">
              {file ? <><FileText size={28} className="text-emerald-brand" /><span className="text-sm truncate max-w-full">{file.name}</span></> : <><UploadCloud size={28} className="text-emerald-brand" /><span className="text-sm">Choose PDF or photo</span></>}
            </button>
            <div className="grid grid-cols-2 gap-3">
              <L label="Date issued / signed"><input data-testid="sdoc-issue" type="date" value={f.issue_date} onChange={(e) => setF({ ...f, issue_date: e.target.value })} className="inp" /></L>
              {target?.entry.has_expiry ? <L label="Expiry / re-check date"><input data-testid="sdoc-expiry" type="date" value={f.expiry_date} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} className="inp" /></L>
                : <L label="Served to tenant on"><input data-testid="sdoc-served" type="date" value={f.served_date} onChange={(e) => setF({ ...f, served_date: e.target.value })} className="inp" /></L>}
            </div>
            <L label="Note (optional)"><input data-testid="sdoc-note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="inp" placeholder="e.g. served by hand with signed receipt" /></L>
            <button data-testid="sdoc-save" disabled={busy} onClick={upload} className="w-full min-h-[48px] rounded-xl bg-emerald-brand hover:bg-emerald-deep text-white font-semibold transition-colors disabled:opacity-60">{busy ? "Uploading…" : "File document"}</button>
          </div>
          <style>{`.inp{width:100%;min-height:44px;padding:0 .75rem;border-radius:.6rem;border:1px solid hsl(var(--input));background:hsl(var(--card))}`}</style>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function L({ label, children }) {
  return <label className="block"><span className="text-xs font-medium text-muted-foreground">{label}</span><div className="mt-1">{children}</div></label>;
}
